import api from './api';
import { 
  SupplierArticle, 
  ArticlesResponse, 
  ArticleStats,
  BulkCreateArticleData,
  BulkCreateResult,
  RequestFieldsData,
  ArticleFilters,
  ExcelImportData,
  ImportResult
} from '../types/article';

// **GET Articles with filtering and pagination**
export const getArticles = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  supplierId?: string;
  ekCode?: string;
  sortBy?: string;
  sortOrder?: string;
  linkedSuppliersOnly?: boolean;
}): Promise<ArticlesResponse> => {
  // Convert boolean to string for backend compatibility
  const queryParams = {
    ...params,
    linkedSuppliersOnly: params.linkedSuppliersOnly ? 'true' : 'false'
  };
  
  console.log('🔍 [articleApi] API Call params:', queryParams);
  console.log('🔍 [articleApi] linkedSuppliersOnly raw:', params.linkedSuppliersOnly);
  console.log('🔍 [articleApi] linkedSuppliersOnly converted:', queryParams.linkedSuppliersOnly);
  
  const response = await api.get('/articles', { params: queryParams });
  return response.data;
};

// **GET Single Article**
export const getArticle = async (id: string): Promise<SupplierArticle> => {
  const response = await api.get(`/articles/${id}`);
  return response.data;
};

// **GET Article by ID (Alias für Kompatibilität)**
export const getArticleById = async (id: string): Promise<SupplierArticle> => {
  return getArticle(id);
};

// **GET Multiple Articles by IDs**
export const getArticlesByIds = async (ids: string[]): Promise<SupplierArticle[]> => {
  if (ids.length === 0) return [];
  
  const response = await api.post('/articles/by-ids', { ids });
  return response.data.articles || [];
};

// **GET All Article IDs (for bulk operations)**
export const getAllArticleIds = async (params: {
  search?: string;
  status?: string;
  supplierId?: string;
  ekCode?: string;
  linkedSuppliersOnly?: boolean;
}): Promise<{
  success: boolean;
  articleIds: string[];
  totalCount: number;
}> => {
  const queryParams = {
    ...params,
    linkedSuppliersOnly: params.linkedSuppliersOnly ? 'true' : 'false'
  };
  
  console.log('🔍 [getAllArticleIds] API Call params:', queryParams);
  
  const response = await api.get('/articles/all-ids', { params: queryParams });
  return response.data;
};

// **POST Bulk Create Articles**
export const bulkCreateArticles = async (data: {
  articles: BulkCreateArticleData[];
  deadline?: string;
  sendNotification?: boolean;
}): Promise<BulkCreateResult> => {
  const response = await api.post('/articles/bulk-create', data);
  return response.data;
};

// **POST Create Single Article**
export const createArticle = async (article: Partial<SupplierArticle>): Promise<SupplierArticle> => {
  const response = await api.post('/articles', article);
  return response.data;
};

// **PUT Update Article**
export const updateArticle = async (id: string, updates: Partial<SupplierArticle>): Promise<SupplierArticle> => {
  const response = await api.put(`/articles/${id}`, updates);
  return response.data;
};

// **PUT Update Article by Supplier (mit Change Tracking)**
export const updateArticleBySupplier = async (
  id: string, 
  updates: Partial<SupplierArticle>, 
  submitToAdmin: boolean = false
): Promise<{
  article: SupplierArticle;
  changedFields: string[];
  submitted: boolean;
}> => {
  const response = await api.put(`/articles/${id}/supplier-update`, {
    ...updates,
    submitToAdmin
  });
  return response.data;
};

// **POST Request Fields**
export const requestArticleFields = async (id: string, data: RequestFieldsData): Promise<{ message: string; requestedFields: string[] }> => {
  const response = await api.post(`/articles/${id}/request-fields`, data);
  return response.data;
};

// **POST Send Reminder**
export const sendArticleReminder = async (id: string, message?: string): Promise<{ message: string }> => {
  const response = await api.post(`/articles/${id}/remind`, { message });
  return response.data;
};

// **GET Article Statistics**
export const getArticleStats = async (): Promise<ArticleStats> => {
  const response = await api.get('/articles/stats');
  return response.data;
};

// **GET Export Articles**
export const exportArticles = async (status: string = 'approved'): Promise<Blob> => {
  const response = await api.get('/articles/export', {
    params: { status },
    responseType: 'blob'
  });
  return response.data;
};

// **GET Article Template Download**
export const downloadArticleTemplate = async (type: 'excel' | 'csv'): Promise<Blob> => {
  const response = await api.get(`/articles/template/download?type=${type}`, {
    responseType: 'blob'
  });
  return response.data;
};

// **DELETE Article**
export const deleteArticle = async (id: string): Promise<{ message: string }> => {
  const response = await api.delete(`/articles/${id}`);
  return response.data;
};

// **Excel Import Functions**
export const importArticlesFromExcel = async (data: ExcelImportData[]): Promise<ImportResult> => {
  const response = await api.post('/articles/import-excel', { articles: data });
  return response.data;
};

export const validateExcelData = async (data: ExcelImportData[]): Promise<ImportResult> => {
  const response = await api.post('/articles/validate-excel', { articles: data });
  return response.data;
};

// **NEW: File-based validation for large Excel files**
export const validateExcelFile = async (file: File): Promise<ImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/articles/validate-excel-file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 120000 // 2 minutes for large files
  });
  return response.data;
};

export const downloadFailedImports = async (errors: any[]): Promise<Blob> => {
  const response = await api.post('/articles/download-failed-imports', 
    { errors },
    { 
      responseType: 'blob',
      timeout: 60000 // 1 minute timeout for Excel generation
    }
  );
  return response.data;
};

// **HELPER: Download exported file**
export const downloadArticleExport = async (status: string = 'approved', filename?: string): Promise<void> => {
  try {
    const blob = await exportArticles(status);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `article_export_${status}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading article export:', error);
    throw error;
  }
};

// **POST Sync from Perfion API**
export const syncFromPerfionAPI = async (): Promise<{
  success: boolean;
  message?: string;
  apiArticles: number;
  processed: number;
  created: number;
  updated: number;
  errors: number;
  totalInDB: number;
  errorDetails?: any[];
}> => {
  const response = await api.post('/articles/sync-perfion');
  return response.data;
};

// **POST Test Perfion Connection**
export const testPerfionConnection = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  const response = await api.post('/articles/test-perfion');
  return response.data;
};

// **CHANGE TRACKING FUNCTIONS**

// **GET Article Changes**
export const getArticleChanges = async (articleNumber: string): Promise<{
  success: boolean;
  changes: any;
  hasUnviewedChanges: boolean;
  lastSubmissionDate?: string;
  changedFields: string[];
}> => {
  const response = await api.get(`/articles/${articleNumber}/changes`);
  return response.data;
};

// **POST Mark Changes as Viewed**
export const markChangesAsViewed = async (articleNumber: string, userId: string): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await api.post(`/articles/${articleNumber}/mark-changes-viewed`, { userId });
  return response.data;
};

// **GET Field History**
export const getFieldHistory = async (articleNumber: string, fieldName: string): Promise<{
  success: boolean;
  fieldHistory: any[];
}> => {
  const response = await api.get(`/articles/${articleNumber}/field-history/${fieldName}`);
  return response.data;
};

export default {
  getArticles,
  getArticle,
  getArticleById,
  getArticlesByIds,
  getAllArticleIds,
  createArticle,
  bulkCreateArticles,
  updateArticle,
  updateArticleBySupplier,
  deleteArticle,
  requestArticleFields,
  sendArticleReminder,
  getArticleStats,
  exportArticles,
  downloadArticleExport,
  importArticlesFromExcel,
  validateExcelData,
  validateExcelFile,
  syncFromPerfionAPI,
  testPerfionConnection,
  // Change Tracking
  getArticleChanges,
  markChangesAsViewed,
  getFieldHistory
};
